import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { AlertTriangle, Clock, FileWarning, LogOut, Stethoscope } from "lucide-react";
import { getJobLeaveStatusLabel } from "utils/jobLeaveStatus";
import type { PersonnelChips as Chips } from "services/personnel.service";
import { formatDay, medicalState } from "./personnel-helpers";

const chipClass = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium";

/** Status chips under a person's name: unsigned Tvarkos docs, approvals, medical, leave. */
export default function PersonnelChips({ chips }: { chips: Chips }) {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "lt") as "lt" | "en";
  const medical = medicalState(chips.medicalValidUntil);
  const leaveActive =
    !!chips.jobLeaveStatus && chips.jobLeaveStatus !== "completed" && chips.jobLeaveStatus !== "cancelled";

  const items: React.ReactNode[] = [];
  if (chips.wsUnsignedCount > 0) {
    items.push(
      <span key="ws" className={clsx(chipClass, "bg-amber-50 text-amber-800")}>
        <AlertTriangle className="h-3 w-3" aria-hidden />
        {t("personnel_chip_ws_unsigned", { n: chips.wsUnsignedCount })}
      </span>
    );
  }
  if (chips.pendingApprovals > 0) {
    items.push(
      <span key="approvals" className={clsx(chipClass, "bg-blue-50 text-blue-800")}>
        <Clock className="h-3 w-3" aria-hidden />
        {t("personnel_chip_pending_approvals", { n: chips.pendingApprovals })}
      </span>
    );
  }
  if (medical === "expired" || medical === "expiring") {
    items.push(
      <span
        key="medical"
        className={clsx(chipClass, medical === "expired" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800")}
      >
        <Stethoscope className="h-3 w-3" aria-hidden />
        {t(medical === "expired" ? "personnel_chip_medical_expired" : "personnel_chip_medical_expiring", {
          date: formatDay(chips.medicalValidUntil),
        })}
      </span>
    );
  }
  if (leaveActive) {
    items.push(
      <span key="leave" className={clsx(chipClass, "bg-orange-50 text-orange-800")}>
        <LogOut className="h-3 w-3" aria-hidden />
        {t("personnel_chip_job_leave", { status: getJobLeaveStatusLabel(chips.jobLeaveStatus!, t, lang) })}
      </span>
    );
  }
  if (chips.hasOnboardingRecord === false) {
    items.push(
      <span key="no-onboarding" className={clsx(chipClass, "bg-gray-100 text-gray-700")}>
        <FileWarning className="h-3 w-3" aria-hidden />
        {t("personnel_chip_no_onboarding_record")}
      </span>
    );
  }
  if (items.length === 0) return null;
  return <div className="mt-1 flex flex-wrap gap-1.5">{items}</div>;
}
